import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DoesNotExistAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUserSignal } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { inviteUser } from '../../features/user-management/api/inviteUser.js';
import { mayIManageUsers } from '../../features/user-management/api/mayIManageUsers.js';
import { sendPasswordResetMail } from '../../features/user-management/api/sendPasswordResetMail.js';
import { setUserArchived } from '../../features/user-management/api/setUserArchived.js';
import { setUserManagementGrantForUser } from '../../features/user-management/api/setUserManagementGrantForUser.js';
import { subscribeManagedUsers } from '../../features/user-management/api/subscribeManagedUsers.js';
import { impersonate } from '../../features/user-management/client/impersonation.js';
import { ManagedUser } from '../../features/user-management/db/ManagedUser.js';

/** every field the list renders — reading one that was not requested would throw */
const USER_SELECTION_SET = {
  _id: 1,
  username: 1,
  email: 1,
  createdAt: 1,
  archivedAt: 1,
  canManageUsers: 1,
} as const;

const ARCHIVED_FIELD = 'archivedAt';
const ADMIN_FIELD = 'canManageUsers';

const formatDate = (date: Date | null | undefined) => (date ? date.toLocaleDateString() : '–');

/** The only filters we offer — the engine's generic filter editor is deliberately not used. */
const QUICK_FILTERS = [
  {
    label: 'Nur aktive Konten',
    getState: (filter: AdornisFilter) => ({
      active: !!filter.getFilterAspect(ARCHIVED_FIELD),
      icon: 'person_check',
    }),
    toggle: (filter: AdornisFilter) => {
      if (filter.getFilterAspect(ARCHIVED_FIELD)) filter.removeFilterAspect(ARCHIVED_FIELD);
      else filter.setFilterAspect(new DoesNotExistAspect({ fieldName: ARCHIVED_FIELD }));
    },
  },
  {
    label: 'Nur Administratoren',
    getState: (filter: AdornisFilter) => ({
      active: !!filter.getFilterAspect(ADMIN_FIELD),
      icon: 'shield_person',
    }),
    toggle: (filter: AdornisFilter) => {
      if (filter.getFilterAspect(ADMIN_FIELD)) filter.removeFilterAspect(ADMIN_FIELD);
      else filter.setFilterAspect(new BooleanAspect({ fieldName: ADMIN_FIELD, value: true }));
    },
  },
];

/**
 * User administration. The sidebar hides the entry, but a hand-typed /users still routes
 * here — so the page asks the server too. Presentation only; the operations re-check.
 */
export const UsersPage = stateful(() => {
  const [invite, setInvite] = useState({ email: '', username: '' });
  const [busy, setBusy] = useState(false);

  const { value: mayManage, loading: gateLoading } = useQuery(() => mayIManageUsers()(), []);
  const myID = currentUserSignal.value?._id;

  // Own View instead of the high-level DataView component: that one always renders the
  // engine's filter editor including its "add filter" button, which this screen has no use
  // for. DataTable is the same engine table underneath.
  const [view] = useState(
    new View<typeof ManagedUser>({
      filter: new AdornisFilter({ aspects: [], searchFields: ['username', 'email'] }),
    }),
    [],
  );
  const filter = view.filter as AdornisFilter;
  // Sorting and the quick filters mutate the filter in place — this is what redraws on it.
  useObservable(() => filter.whenChanging ?? of(null), [filter]);
  const searchForm = useForm(filter, {}, [filter]);

  const { document: inviteDoc, field } = useForm(invite, {}, [invite]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (e: any) {
      XSnackbar.show(e?.message ?? 'Die Aktion ist fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  // Memoised: DataView keys its option building on this array's identity, so a fresh
  // array every render would rebuild the whole view.
  const columns = useMemo<ColumnDefinition<typeof ManagedUser>[]>(
    () => [
      [
        {
          fieldName: 'username',
          columnName: 'Name',
          renderItem: (user: ManagedUser) => html`
            <span class="font-medium">${user.username ?? '–'}</span>
            ${user._id === myID ? html`<span class="badge badge-ghost badge-sm ml-2">du</span>` : nothing}
          `,
        },
        { fieldName: 'email', columnName: 'E-Mail' },
        {
          fieldName: 'createdAt',
          columnName: 'Angelegt',
          renderItem: (user: ManagedUser) => html`${formatDate(user.createdAt)}`,
        },
        {
          fieldName: ARCHIVED_FIELD,
          columnName: 'Status',
          renderItem: (user: ManagedUser) =>
            user.archivedAt
              ? html`<span class="badge badge-sm">Deaktiviert</span>`
              : html`<span class="badge badge-success badge-sm">Aktiv</span>`,
        },
        {
          fieldName: ADMIN_FIELD,
          columnName: 'Rolle',
          renderItem: (user: ManagedUser) =>
            user.canManageUsers ? html`<span class="badge badge-outline badge-sm">Admin</span>` : html`–`,
        },
        {
          customID: 'actions',
          columnName: 'Aktionen',
          isSortable: false,
          columnWidth: 'max-content',
          renderItem: (user: ManagedUser) => {
            const isSelf = user._id === myID;
            const archived = !!user.archivedAt;
            return html`
              <div class="flex gap-1 justify-end w-full pr-2">
                <button
                  class="btn btn-ghost btn-xs"
                  title=${archived
                    ? 'Deaktivierte Konten können nicht übernommen werden'
                    : 'Sitzung als dieses Konto fortsetzen'}
                  ?disabled=${isSelf || archived}
                  @click=${() =>
                    run(async () => {
                      const name = await impersonate(user._id);
                      // This page is administrators-only and the session is no longer one —
                      // leave before its own gate turns the screen into "Keine Berechtigung".
                      goTo('/mails');
                      XSnackbar.show(`Du arbeitest jetzt als ${name}.`);
                    })}
                >
                  <x-icon class="text-sm">switch_account</x-icon>
                </button>
                <button
                  class="btn btn-ghost btn-xs"
                  title="Passwort-Link per E-Mail senden"
                  @click=${() =>
                    run(async () => {
                      const to = await sendPasswordResetMail(user._id)();
                      XSnackbar.show(`Passwort-Link an ${to} gesendet.`);
                    })}
                >
                  <x-icon class="text-sm">key</x-icon>
                </button>
                <button
                  class="btn btn-ghost btn-xs"
                  title=${user.canManageUsers ? 'Benutzerverwaltung entziehen' : 'Benutzerverwaltung freigeben'}
                  ?disabled=${isSelf && !!user.canManageUsers}
                  @click=${() =>
                    run(async () => {
                      await setUserManagementGrantForUser(user._id, !user.canManageUsers)();
                      XSnackbar.show(user.canManageUsers ? 'Berechtigung entzogen.' : 'Berechtigung erteilt.');
                    })}
                >
                  <x-icon class="text-sm">
                    ${user.canManageUsers ? 'admin_panel_settings' : 'shield_person'}
                  </x-icon>
                </button>
                <button
                  class="btn btn-ghost btn-xs ${archived ? '' : 'text-error'}"
                  title=${archived ? 'Konto wieder aktivieren' : 'Konto deaktivieren'}
                  ?disabled=${isSelf}
                  @click=${() =>
                    run(async () => {
                      await setUserArchived(user._id, !archived)();
                      XSnackbar.show(archived ? 'Konto wieder aktiviert.' : 'Konto deaktiviert.');
                    })}
                >
                  <x-icon class="text-sm">${archived ? 'person_check' : 'person_off'}</x-icon>
                </button>
              </div>
            `;
          },
        },
      ],
    ],
    [myID],
  );

  const tableOptions = useMemo(
    () => [
      buildFullOptions({
        class: ManagedUser,
        operation: subscribeManagedUsers,
        view,
        selectionSet: USER_SELECTION_SET,
        customColumns: columns,
        isEntryClickable: false,
      }).dataViewOptions,
    ],
    [columns, view],
  );

  // stateful needs a stable hook order, so the early returns come after every hook above.
  if (gateLoading) return html`<div class="p-8 text-center text-base-content/50">Wird geladen …</div>`;
  if (!mayManage)
    return html`
      <div class="p-6 max-w-3xl mx-auto">
        <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm p-8 text-center">
          <x-icon class="text-4xl text-base-content/30">lock</x-icon>
          <h1 class="text-lg font-semibold mt-3">Keine Berechtigung</h1>
          <p class="text-base-content/60 mt-1">
            Für die Benutzerverwaltung fehlt deinem Konto die Berechtigung. Wende dich an einen Administrator.
          </p>
          <a class="btn btn-primary btn-sm mt-5" href="/mails">Zurück zu den Mails</a>
        </div>
      </div>
    `;

  const handleInvite = () =>
    run(async () => {
      const email = (inviteDoc.email ?? '').trim();
      if (!email) {
        XSnackbar.show('Bitte eine E-Mail-Adresse angeben.');
        return;
      }
      const to = await inviteUser(email, (inviteDoc.username ?? '').trim())();
      setInvite({ email: '', username: '' });
      XSnackbar.show(`Benutzer angelegt — Einladung an ${to} gesendet.`);
    });

  return html`
    <div class="p-6 max-w-6xl mx-auto flex flex-col gap-6">
      <div>
        <h1 class="text-2xl font-bold tracking-tight">Benutzer</h1>
        <p class="text-base-content/60 mt-1">
          Konten für dieses Dashboard anlegen, deaktivieren und Passwörter zurücksetzen.
        </p>
      </div>

      <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm">
        <div class="px-5 py-4 border-b border-base-200">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-base-content/50">Neuen Benutzer anlegen</h2>
        </div>
        <div class="p-5">
          <div class="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
            ${TextInput({ ...(field('email') as any), placeholder: 'E-Mail-Adresse', type: 'email' })}
            ${TextInput({ ...(field('username') as any), placeholder: 'Name (optional)' })}
            <button class="btn btn-primary" ?disabled=${busy} @click=${handleInvite}>
              <x-icon class="text-base">person_add</x-icon>
              Anlegen
            </button>
          </div>
          <p class="text-sm text-base-content/60 mt-3">
            Es wird kein Passwort vergeben. Die Person erhält per E-Mail einen einmaligen Link, über den sie ihr
            eigenes Passwort setzt.
          </p>
        </div>
      </div>

      <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm overflow-hidden">
        <div class="flex items-center gap-2 px-6 py-3 border-b border-base-200">
          <div class="grow">
            ${TextInput({ ...(searchForm.field('search') as any), placeholder: 'Nach Name oder E-Mail suchen…' })}
          </div>
          ${QUICK_FILTERS.map(quickFilter => {
            const state = quickFilter.getState(filter);
            return html`
              <button
                type="button"
                class="btn btn-sm btn-square ${state.active ? 'btn-primary' : 'btn-ghost'}"
                title=${quickFilter.label}
                @click=${() => quickFilter.toggle(filter)}
              >
                <x-icon class="text-base">${state.icon}</x-icon>
              </button>
            `;
          })}
        </div>
        <div class="overflow-x-auto px-2 pb-2">${DataTable({ options: tableOptions })}</div>
      </div>
    </div>
  `;
});
